import React, { useRef, useState } from 'react';
import { PageTitle } from '@cvent/carina/components/Navigation/PageTitle';
import { TitleBar } from '@cvent/carina/components/Navigation/TitleBar';
import { AppSwitcherMenuItem } from '@cvent/carina/components/Navigation/AppSwitcherMenuItem';
import { AppSwitcher } from '@cvent/carina/components/Navigation/AppSwitcher';
import { ICONS } from '@cvent/carina/components/Navigation/utils';
import { NavigationIcon as Icon } from '@cvent/carina/components/Navigation/NavigationIcon';
import { GlobalNavigationLink as GlobalNavLink } from '@cvent/carina/components/Navigation/GlobalNavigationLink';
import { Search } from '@cvent/carina/components/Navigation/Search';
import { ProductTitle } from '@cvent/carina/components/Navigation/ProductTitle';
import { Divider } from '@cvent/carina/components/Navigation/Divider';
import { Logo } from '@cvent/carina/components/Navigation/Logo';
import { NavigationButton } from '@cvent/carina/components/Navigation/NavigationButton';
import { TopNavigation as TopNav } from '@cvent/carina/components/Navigation/TopNavigation';
import { useNavigation } from '@cvent/carina/components/Navigation/NavigationProvider';
import MenuIcon from '@cvent/carina/components/Icon/Menu';
import { useBreakpoints } from '@cvent/carina/components/Breakpoints/BreakpointsProvider';
import { OnWhen } from '@cvent/carina/components/OnWhen';
import { Col } from '@cvent/carina/components/Col';
import { Row } from '@cvent/carina/components/Row';
import NextLink from 'next/link';

type topNavProps = {
  hasLocalNav: boolean;
  isLocalNavOpen: boolean;
  toggleNav: () => void;
};

export function TopNavigation({ hasLocalNav, toggleNav, isLocalNavOpen }: topNavProps): JSX.Element {
  const { logo, page, search, appSwitcher, onSearch, title, globalNav } = useNavigation();
  const searchRef = useRef();
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { isL, isM, isS, isDefaultSize } = useBreakpoints();

  const appSwitcherData = [...appSwitcher.items];
  if (isS || isM || isDefaultSize) {
    appSwitcherData.push(...globalNav);
  }

  return (
    <>
      <TopNav hasOverlay={isSearchOpen}>
        <div css={{ display: 'flex', justifyContent: 'space-between', width: '100%', height: '100%' }}>
          <div
            css={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              maxWidth: isS || isM || isDefaultSize ? '70%' : '40%'
            }}
          >
            <Logo
              logo={logo}
              // eslint-disable-next-line react/no-unstable-nested-components
              Link={({ item, children }): JSX.Element => (
                <NextLink href={item.url} passHref>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a>{children}</a>
                </NextLink>
              )}
            />
            <OnWhen m l xl>
              <Divider />
              <ProductTitle title={title} />
            </OnWhen>
          </div>
          <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <OnWhen l xl>
              <ul
                role="menu"
                css={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  transform: `translate(${isL ? -50 : -100}px, 0)`
                }}
              >
                {globalNav
                  .filter(item => item?.url?.href)
                  .map(item => (
                    <GlobalNavLink key={item.title}>
                      <NextLink href={item.url.href} passHref>
                        <a href={item.url.href}>{item.title}</a>
                      </NextLink>
                    </GlobalNavLink>
                  ))}
              </ul>
            </OnWhen>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: 0 }}>
            <Search
              accessibilityLabel={search.title}
              clearButtonAccessibilityLabel={search.clearSearchTextLabel}
              openFlyoutAccessibilityLabel={search.moreFiltersLabel}
              searchInputAccessibilityLabel={search.title}
              inputRef={searchRef}
              searchTerm={searchTerm}
              onChange={(val: string): void => setSearchTerm(val)}
              isSearchOpen={isSearchOpen}
              clearSearchTerm={(): void => setSearchTerm('')}
              setIsSearchOpen={setIsSearchOpen}
              onSearch={(): void => onSearch(searchTerm)}
            />
            <AppSwitcher accessibilityLabel={appSwitcher.title}>
              {(closeMenu): JSX.Element => (
                <div>
                  {appSwitcherData?.map((item, idx) => (
                    <AppSwitcherMenuItem key={item.title}>
                      <NextLink href={item.url.href} passHref>
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <a
                          css={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                          tabIndex={0}
                          role="menuitem"
                          onBlur={(): void => {
                            if (idx === appSwitcherData.length - 1) closeMenu();
                          }}
                          key={item.title}
                        >
                          <span>
                            {item.icon ? <Icon icon={ICONS[item.icon]} /> : <span />}
                            {item.title}
                          </span>
                        </a>
                      </NextLink>
                    </AppSwitcherMenuItem>
                  ))}
                </div>
              )}
            </AppSwitcher>
          </div>
        </div>
      </TopNav>
      <TitleBar>
        <Row>
          <Col
            width="fill"
            flex={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start'
            }}
          >
            {hasLocalNav && (
              <NavigationButton
                onClick={toggleNav}
                appearance="secondary"
                accessibilityLabel="toggle local navigation"
                onKeyPress={(e): void => {
                  if (e.key === 'Enter') toggleNav();
                }}
                /* we are updating the typing on icons exported from carina-icon to address the issue */
                icon={MenuIcon}
                aria-expanded={isLocalNavOpen}
              />
            )}
            <PageTitle title={page} />
          </Col>
        </Row>
      </TitleBar>
    </>
  );
}
